// Sugalarm — Cycle 74 rework: the slider is now a read-only visual
// reference and the four cards beneath are the primary editor.
//
// Pre-cycle (cycle 40) used a select-then-drag model where the user
// tapped a card or thumb to make it draggable and then dragged it
// along the track. On the kiosk's 1024×600 touch screen the adjacent
// thumbs still made it hard to land on an exact value — a "set urgent
// low to 70" became a rage-tap exercise. Cycle 74 demotes the track
// + thumbs to a static visual and promotes the four cards underneath
// to stepper editors: each card has a − and a + button (with
// auto-repeat on long-press), tap-to-focus, and keyboard arrow
// support. The slider's gradient + zone bands remain — they're the
// visual gestalt of the four thresholds — and the four thumbs render
// as live position markers that update as the steppers fire.
//
// The component's external contract is preserved exactly:
//   forwardRef → ThresholdSliderHandle.reset()
//   onCommit(key, next) — fires on every value change
//   onDirtyChange(dirty)
// so `ThresholdsSubsection` (the Save/Reset action-row owner) keeps
// working without changes. The cycle-40 batched-PATCH Save button
// still observes the dirty flag the same way; mouse/keyboard
// increments fire onCommit just like the cycle-40 pointerup did.
//
// The local clamp invariant `urgent_low < target_low < target_high <
// urgent_high` (with MIN_GAP = 1) is preserved — server still does
// the authoritative check on PATCH.

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { Numpad } from "../../components/ui/Numpad";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import type { AlarmZone as AlarmSoundZone } from "../../lib/alarmSoundOptions";
import { toMmol } from "../../lib/glucose";
import styles from "./ThresholdSlider.module.css";
import {
  DRAG_STEP_MGDL,
  HANDLE_ROW,
  STEPPER_STEP_MGDL,
  snapToStep,
} from "./thresholdHandleLayout";

const MGDL_PER_MMOL = 18.0182;

type Key = "urgent_low" | "target_low" | "target_high" | "urgent_high";

export interface ThresholdValues {
  urgent_low: number;
  target_low: number;
  target_high: number;
  urgent_high: number;
}

export interface ThresholdSliderHandle {
  /** Snap the local draft back to the current `value` prop. Used by
   *  the Settings Reset button. */
  reset: () => void;
  /** Cycle 74: snap the four thresholds to the system defaults
   *  (urgent_low=55 / target_low=70 / target_high=180 / urgent_high=250)
   *  and fire `onCommit` for each key whose value changed. Used by the
   *  "Reset to default" button. */
  resetToDefaults: () => void;
}

/** System defaults — mirrors `server/schemas/settings.py:DEFAULTS`. */
export const THRESHOLD_DEFAULTS: ThresholdValues = {
  urgent_low: 55,
  target_low: 70,
  target_high: 180,
  urgent_high: 250,
};

interface Props {
  value: ThresholdValues;
  /** Fires on every value change — stepper press, keyboard arrow,
   *  auto-repeat tick. The parent re-issues the value to the server
   *  via PATCH /api/settings. */
  onCommit: (key: Key, next: number) => void;
  min?: number;
  max?: number;
  /** Display preference. Internal arithmetic stays mg/dL — values are
   *  posted as mg/dL on the wire — but the rendered numbers convert
   *  to mmol/L when set. */
  units?: "mg/dL" | "mmol/L";
  /** Fires whenever the draft becomes different-from / equal-to the
   *  incoming `value` prop. The parent uses this to enable / disable
   *  its Save + Reset buttons. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Cycle 129: drives numeric entry — kiosk taps open the custom
   *  `Numpad`; mobile/web use a native `<input inputmode>` (the OS pad). */
  variant?: FrameVariant;
  /** Cycle 242: render the per-zone custom-sound picker (dropdown +
   *  play/stop preview) inside each card's lower section. When supplied,
   *  an extra "In Range" card is inserted between Low and High (it has no
   *  threshold stepper — just the label, dot, a static range readout, and
   *  the picker). Omitting it renders the original four-card layout. */
  renderSoundPicker?: (zone: AlarmSoundZone) => ReactNode;
}

const DEFAULT_MIN = 40;
const DEFAULT_MAX = 400;
const MIN_GAP = 1;

// Long-press auto-repeat cadence — a coarse-then-fine acceleration so
// dragging 70 → 95 doesn't require 25 separate taps. Initial delay,
// medium step interval, then a faster fine interval after the user
// has been holding for ~1.5 s.
const REPEAT_INITIAL_DELAY_MS = 400;
const REPEAT_MEDIUM_INTERVAL_MS = 80;
const REPEAT_FINE_AFTER_MS = 1500;
const REPEAT_FINE_INTERVAL_MS = 30;

const ORDER: Key[] = ["urgent_low", "target_low", "target_high", "urgent_high"];

// Cycle 242: threshold key → alarm-sound zone (the In Range zone has no
// threshold key — it's the inserted card).
const ZONE_FOR_KEY: Record<Key, AlarmSoundZone> = {
  urgent_low: "urgent_low",
  target_low: "low",
  target_high: "high",
  urgent_high: "urgent_high",
};
const META: Record<
  Key,
  { label: string; color: string; cardLabel: string }
> = {
  urgent_low: {
    label: "urgent low",
    color: "var(--zone-urgent)",
    cardLabel: "Urgent low",
  },
  target_low: {
    label: "low",
    color: "var(--zone-warn)",
    cardLabel: "Low",
  },
  target_high: {
    label: "high",
    color: "var(--zone-warn)",
    cardLabel: "High",
  },
  urgent_high: {
    label: "urgent high",
    color: "var(--zone-urgent)",
    cardLabel: "Urgent high",
  },
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function valuesEqual(a: ThresholdValues, b: ThresholdValues): boolean {
  return (
    a.urgent_low === b.urgent_low &&
    a.target_low === b.target_low &&
    a.target_high === b.target_high &&
    a.urgent_high === b.urgent_high
  );
}

export const ThresholdSlider = forwardRef<ThresholdSliderHandle, Props>(
  function ThresholdSlider(
    {
      value,
      onCommit,
      min = DEFAULT_MIN,
      max = DEFAULT_MAX,
      units = "mg/dL",
      onDirtyChange,
      variant = "web",
      renderSoundPicker,
    },
    ref,
  ) {
    const isKiosk = variant === "kiosk";
    const fmt = (mgdl: number): string =>
      units === "mmol/L" ? toMmol(mgdl).toFixed(1) : String(mgdl);
    const [draft, setDraft] = useState<ThresholdValues>(value);
    // Cycle 129: in-progress numeric entry for the tapped value card —
    // kiosk drives the custom Numpad, mobile/web a native input. Held as
    // a display-unit string and only converted + clamped + committed on
    // commit (NOT per keystroke, so multi-digit + mmol entry is clean).
    const [editing, setEditing] = useState<{ key: Key; str: string } | null>(
      null,
    );
    // Active drag handle (pointer drag on the range bar).
    const [dragKey, setDragKey] = useState<Key | null>(null);
    const trackRef = useRef<HTMLDivElement>(null);

    // Cycle 400: which pill is mid-pulse after a stepper press, so the
    // user can see WHICH handle a `−` / `+` moved. Purely cosmetic; the
    // pill's shape never changes.
    const [pulseKey, setPulseKey] = useState<Key | null>(null);
    const pulseTimerRef = useRef<number | null>(null);
    const pulse = useCallback((key: Key) => {
      setPulseKey(key);
      if (pulseTimerRef.current != null) {
        window.clearTimeout(pulseTimerRef.current);
      }
      pulseTimerRef.current = window.setTimeout(() => setPulseKey(null), 220);
    }, []);
    useEffect(
      () => () => {
        if (pulseTimerRef.current != null) {
          window.clearTimeout(pulseTimerRef.current);
        }
      },
      [],
    );

    // Cycle 400: the track's live LAYOUT width, used only to clamp the
    // drag callout inside the track. Pills position themselves in
    // percent, so nothing else needs a pixel measurement.
    //
    // ⚠ `offsetWidth`, NEVER `getBoundingClientRect().width`, FOR ANYTHING
    // THAT FEEDS A CSS `px` VALUE. The kiosk paints its 1024x640 design box
    // through `transform: scale(1.25)`, and getBoundingClientRect reports
    // SCREEN px — so a `left` computed from it lands 1.25x too far right.
    // Found on the bench: every pill sat a quarter of its distance from
    // the track's left edge past its own value, which is why the markers
    // never lined up with the zone-band boundaries (those use percent).
    // getBoundingClientRect is fine for RATIOS (a pointer x against the
    // track), which is how dragTo uses it. Same family as the cycle-395
    // `vh` trap: inside the stage, only design-box units are honest.
    const [trackWidth, setTrackWidth] = useState(0);

    // The callout's own half-width, MEASURED — "urgent high" is ~114px wide
    // and "low" ~70px, so a fixed clamp either clips the wide one at the
    // track edge or over-clamps the narrow one. Read after every paint of
    // the callout (its text changes with the value and the zone).
    const calloutRef = useRef<HTMLDivElement>(null);
    const [calloutHalf, setCalloutHalf] = useState(46);
    useEffect(() => {
      const el = calloutRef.current;
      if (!el) return;
      const half = el.offsetWidth / 2;
      setCalloutHalf((prev) =>
        half > 0 && Math.abs(half - prev) > 0.5 ? half : prev,
      );
      // The callout's text — and so its width — depends on which pill is
      // being dragged and on its current value.
    }, [dragKey, draft]);

    useEffect(() => {
      const el = trackRef.current;
      if (!el) return;
      const read = () => setTrackWidth(el.offsetWidth);
      read();
      if (typeof ResizeObserver === "undefined") return;
      const ro = new ResizeObserver(read);
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    // If the parent updates (e.g. a fresh GET /api/settings response or
    // a successful PATCH echo) replace the draft so the UI reflects the
    // source of truth. Clears the dirty flag naturally.
    useEffect(() => {
      setDraft(value);
    }, [value.urgent_low, value.target_low, value.target_high, value.urgent_high]); // eslint-disable-line react-hooks/exhaustive-deps

    // Notify the parent on dirty-flag transitions.
    const dirty = !valuesEqual(draft, value);
    const dirtyRef = useRef(false);
    useEffect(() => {
      if (dirtyRef.current === dirty) return;
      dirtyRef.current = dirty;
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          setDraft(value);
        },
        resetToDefaults: () => {
          // Fire onCommit for each key whose default differs from the
          // current value, so the parent PATCHes through. Compute the
          // diff against `value` (the source of truth), not `draft`,
          // so a mid-edit reset still ends up at defaults.
          (Object.keys(THRESHOLD_DEFAULTS) as Key[]).forEach((k) => {
            if (value[k] !== THRESHOLD_DEFAULTS[k]) {
              onCommit(k, THRESHOLD_DEFAULTS[k]);
            }
          });
          setDraft(THRESHOLD_DEFAULTS);
        },
      }),
      [value, onCommit],
    );

    const valueToPct = useCallback(
      (v: number): number => ((clamp(v, min, max) - min) / (max - min)) * 100,
      [min, max],
    );

    const clampForKey = useCallback(
      (key: Key, raw: number, current: ThresholdValues): number => {
        const lower =
          key === "urgent_low"
            ? min
            : key === "target_low"
              ? current.urgent_low + MIN_GAP
              : key === "target_high"
                ? current.target_low + MIN_GAP
                : current.target_high + MIN_GAP;
        const upper =
          key === "urgent_high"
            ? max
            : key === "target_high"
              ? current.urgent_high - MIN_GAP
              : key === "target_low"
                ? current.target_high - MIN_GAP
                : current.target_low - MIN_GAP;
        return clamp(raw, lower, upper);
      },
      [min, max],
    );

    // Single source of truth for "step this key by delta, clamp,
    // commit if changed." Used by tap, keyboard, and auto-repeat.
    const stepKey = useCallback(
      (key: Key, delta: number) => {
        setDraft((prev) => {
          const next = clampForKey(key, prev[key] + delta, prev);
          if (next === prev[key]) return prev;
          onCommit(key, next);
          return { ...prev, [key]: next };
        });
      },
      [clampForKey, onCommit],
    );

    // Auto-repeat machinery. One timer set is shared across every
    // stepper button — only one button can be held at a time (no
    // multi-touch threshold-editing) so a single ref is enough.
    const repeatRef = useRef<{
      timeout: number | null;
      interval: number | null;
      escalate: number | null;
    }>({ timeout: null, interval: null, escalate: null });

    const stopRepeat = useCallback(() => {
      const r = repeatRef.current;
      if (r.timeout != null) {
        window.clearTimeout(r.timeout);
        r.timeout = null;
      }
      if (r.interval != null) {
        window.clearInterval(r.interval);
        r.interval = null;
      }
      if (r.escalate != null) {
        window.clearTimeout(r.escalate);
        r.escalate = null;
      }
    }, []);

    const startRepeat = useCallback(
      (key: Key, delta: number) => {
        stopRepeat();
        const r = repeatRef.current;
        // After REPEAT_INITIAL_DELAY_MS, kick off the medium cadence;
        // after REPEAT_FINE_AFTER_MS swap to the fine cadence so a
        // sustained hold accelerates rather than dragging the user
        // through 30+ taps.
        r.timeout = window.setTimeout(() => {
          stepKey(key, delta);
          r.interval = window.setInterval(
            () => stepKey(key, delta),
            REPEAT_MEDIUM_INTERVAL_MS,
          );
          r.escalate = window.setTimeout(() => {
            if (r.interval != null) {
              window.clearInterval(r.interval);
            }
            r.interval = window.setInterval(
              () => stepKey(key, delta),
              REPEAT_FINE_INTERVAL_MS,
            );
          }, REPEAT_FINE_AFTER_MS - REPEAT_INITIAL_DELAY_MS);
        }, REPEAT_INITIAL_DELAY_MS);
      },
      [stepKey, stopRepeat],
    );

    // Cleanup on unmount in case the user pulls away from the page
    // mid-press without firing pointerup / blur.
    useEffect(() => {
      return () => stopRepeat();
    }, [stopRepeat]);

    const onStepPointerDown = (key: Key, delta: number) =>
      (e: React.PointerEvent<HTMLButtonElement>) => {
        // Don't preventDefault — let the focus + click fire normally so
        // keyboard activation still works. Auto-repeat is the long-hold
        // affordance; the single-press is the immediate effect on
        // pointerdown so the user gets instant feedback (the cycle-40
        // "auto-commit on pointerup" gave a 100-200ms gap that read as
        // sluggish).
        if (e.button !== undefined && e.button !== 0) return;
        stepKey(key, delta);
        // Cycle 400: show WHICH pill a stepper press moved. The pill
        // does not change shape — it is the same shape in every state.
        pulse(key);
        startRepeat(key, delta);
      };

    const onStepRelease = () => stopRepeat();

    // Cycle 129: commit a tapped-card numeric edit. Parse the display
    // string (mmol → mg/dL when needed), clamp against the order
    // invariant, and onCommit if changed. mg/dL stays on the wire.
    const commitEdit = useCallback(() => {
      setEditing((ed) => {
        if (!ed) return null;
        const n = parseFloat(ed.str);
        if (Number.isFinite(n)) {
          const mgdl =
            units === "mmol/L" ? Math.round(n * MGDL_PER_MMOL) : Math.round(n);
          setDraft((prev) => {
            const next = clampForKey(ed.key, mgdl, prev);
            if (next === prev[ed.key]) return prev;
            onCommit(ed.key, next);
            return { ...prev, [ed.key]: next };
          });
        }
        return null;
      });
    }, [clampForKey, onCommit, units]);

    // Cycle 129: drag a handle along the range bar. During the drag only
    // the local `draft` updates (smooth, clamped per the order rule); the
    // value is committed ONCE on release — so a drag fires a single
    // PATCH, matching the discrete-commit posture of the steppers (no
    // per-pointermove PATCH storm).
    //
    // Cycle 400: the in-flight key lives in a REF. The cycle-129 move
    // handler gated on `dragKey !== key` read from the render closure, so
    // the first moves after pointerdown were dropped until React had
    // flushed `setDragKey`. A ref is read at event time and cannot be
    // stale.
    const dragCommitRef = useRef<{ key: Key; val: number } | null>(null);
    const dragKeyRef = useRef<Key | null>(null);

    /** Drag-start slop, in DESIGN px: the pointer must travel this far
     *  from the press before the value moves at all. A real thumb never
     *  holds perfectly still, and with a 5 mg/dL drag grid a stepper-set
     *  72 would otherwise re-snap to 70 from one pixel of jitter and
     *  commit on release. Scaled to screen px at press time. */
    const DRAG_SLOP_PX = 6;
    const dragStartXRef = useRef(0);
    const dragArmedRef = useRef(false);
    const dragSlopScreenRef = useRef(DRAG_SLOP_PX);

    /** Snap a client x to a mg/dL value on the track. Drag snaps to the
     *  coarse grid; the order-clamp is applied by the caller and WINS
     *  over the grid — a UI that refuses a value the server accepts is
     *  a bug, so landing on `neighbour ± MIN_GAP` off-grid is correct. */
    const valueAtX = useCallback(
      (clientX: number): number | null => {
        // A non-finite x is a broken event, not a position — ignore the
        // move rather than let the snap's floor fallback pull a threshold
        // toward 40 (review round, safety advisory).
        if (!Number.isFinite(clientX)) return null;
        const rect = trackRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) return null;
        const pct = clamp((clientX - rect.left) / rect.width, 0, 1);
        return snapToStep(min + pct * (max - min), DRAG_STEP_MGDL, min, max);
      },
      [min, max],
    );

    const dragTo = useCallback(
      (key: Key, clientX: number) => {
        const raw = valueAtX(clientX);
        if (raw == null) return;
        setDraft((prev) => {
          const next = clampForKey(key, raw, prev);
          if (next === prev[key]) return prev;
          dragCommitRef.current = { key, val: next };
          return { ...prev, [key]: next };
        });
      },
      [clampForKey, valueAtX],
    );

    /** Press x minus the grabbed handle's own x, so a pill tracks the
     *  finger from where it already sits instead of teleporting under it.
     *  Merely touching a handle never changes its value. */
    const dragOffsetRef = useRef(0);

    /** A value's position as a percentage of the track — scale-independent,
     *  the same unit the zone bands use. */
    const truePct = (k: Key): number => valueToPct(draft[k]);

    // Cycle 400 (rethought on the bench): DRAG THE PILL, AND ONLY THE PILL.
    //
    // The first design put a transparent grab band over the whole row so a
    // press anywhere would move "the nearest handle". On real glass that
    // meant every stray tap in the region edited something, and once the
    // layout resolver had nudged a crowded pill off its value, "nearest"
    // no longer meant the pill under the finger. Both are gone: a pill is
    // always centred on its own value, the pill itself is the only thing
    // that takes a pointerdown, and a tap on bare track does nothing.
    const onPillDown = (key: Key) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== undefined && e.button !== 0) return;
      if (editing) return;
      // One pill at a time. A second finger landing mid-drag would
      // otherwise discard the first pill's pending commit while its draft
      // stayed moved (review round, safety advisory).
      if (dragKeyRef.current) return;
      e.preventDefault();
      // Capture on the pill: the pointer leaves it on any real drag (and
      // drifts vertically — the reported gesture), and capture is what keeps
      // the moves coming regardless.
      e.currentTarget.setPointerCapture(e.pointerId);
      dragCommitRef.current = null;
      dragKeyRef.current = key;
      setDragKey(key);
      // Screen space on both sides: the pill's screen x is the track's
      // screen left plus the value's FRACTION of the track's screen width.
      const rect = trackRef.current?.getBoundingClientRect();
      dragOffsetRef.current = rect
        ? e.clientX - (rect.left + (truePct(key) / 100) * rect.width)
        : 0;
      dragStartXRef.current = e.clientX;
      dragArmedRef.current = false;
      // Screen px per design px — the kiosk stage is painted through
      // transform: scale(), so a 6px slop must be 7.5 screen px there.
      const el = trackRef.current;
      const scale = el && rect && el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      dragSlopScreenRef.current = DRAG_SLOP_PX * scale;
    };

    const onPillMove = (e: React.PointerEvent<HTMLDivElement>) => {
      const key = dragKeyRef.current;
      if (!key) return;
      if (!dragArmedRef.current) {
        if (Math.abs(e.clientX - dragStartXRef.current) < dragSlopScreenRef.current) return;
        dragArmedRef.current = true;
      }
      // Deliberately NOT gated on the pointer remaining inside any
      // element's bounds.
      dragTo(key, e.clientX - dragOffsetRef.current);
    };

    const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
      dragKeyRef.current = null;
      setDragKey(null);
      if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    };

    /** pointerup = the user let go on purpose: commit ONCE. */
    const onPillUp = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragKeyRef.current) return;
      endDrag(e);
      const c = dragCommitRef.current;
      dragCommitRef.current = null;
      if (c) onCommit(c.key, c.val);
    };

    /** pointercancel = the browser/OS took the gesture away (a reload, a
     *  system dialog, the app backgrounding). The user did NOT confirm the
     *  value under their finger, so REVERT to the last committed value
     *  instead of committing it. Caught on the bench: a rebuild reloaded
     *  the page while the operator was holding two pills at the ends of
     *  the track, and urgent_low=40 / urgent_high=400 — the two "never
     *  alarm" extremes — went to the server. A threshold is an alarm
     *  setting; a half-made gesture must never write one. */
    const onPillCancel = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragKeyRef.current) return;
      endDrag(e);
      dragCommitRef.current = null;
      setDraft(value);
    };

    const onPillKeyDown = (k: Key) => (e: React.KeyboardEvent) => {
      const d =
        e.key === "ArrowLeft" || e.key === "ArrowDown"
          ? -STEPPER_STEP_MGDL
          : e.key === "ArrowRight" || e.key === "ArrowUp"
            ? STEPPER_STEP_MGDL
            : 0;
      if (d === 0) return;
      e.preventDefault();
      stepKey(k, d);
      pulse(k);
    };

    const renderPill = (k: Key) => (
      <div
        key={k}
        className={[
          styles.pillHit,
          dragKey === k ? styles.pillHitActive : "",
          pulseKey === k ? styles.pillHitPulse : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ left: `${truePct(k)}%`, color: META[k].color }}
        role="slider"
        tabIndex={0}
        aria-label={`${META[k].cardLabel} threshold`}
        aria-valuenow={draft[k]}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuetext={`${fmt(draft[k])} ${units}`}
        onKeyDown={onPillKeyDown(k)}
        onPointerDown={onPillDown(k)}
        onPointerMove={onPillMove}
        onPointerUp={onPillUp}
        onPointerCancel={onPillCancel}
      >
        <span className={styles.pill}>{fmt(draft[k])}</span>
        <span className={styles.tick} aria-hidden="true" />
      </div>
    );

    return (
      <div>
        {/* Cycle 400: the range bar — five colored zone bands and four
            LABELLED PILLS, each centred exactly on its own value and
            staggered above/below the track, joined to it by a short
            straight tick. Only a pill takes a pointerdown. The four cards
            below remain the primary keyboard-accessible editor. */}
        <div className={styles.wrap}>
          {/* Reserved band so the drag callout never overlays the
              subsection header above. Always present, so the layout does
              not shift when a drag starts. */}
          <div className={styles.calloutBand}>
            {dragKey && trackWidth > 0 && (
              <div
                ref={calloutRef}
                className={styles.callout}
                style={{
                  left: `${
                    trackWidth > 0
                      ? clamp(
                          truePct(dragKey),
                          (calloutHalf / trackWidth) * 100,
                          100 - (calloutHalf / trackWidth) * 100,
                        )
                      : truePct(dragKey)
                  }%`,
                  color: META[dragKey].color,
                }}
              >
                <span className={styles.calloutLabel}>
                  {META[dragKey].label}
                </span>
                <span className={styles.calloutValue}>{fmt(draft[dragKey])}</span>
              </div>
            )}
          </div>

          <div className={styles.rowBox}>
            <div className={styles.pillRowUpper}>
              {ORDER.filter((k) => HANDLE_ROW[k] === "upper").map(renderPill)}
            </div>

          <div className={styles.track} ref={trackRef}>
            <div className={styles.bandClip}>
            <div
              className={styles.band}
              style={{
                left: 0,
                width: `${valueToPct(draft.urgent_low)}%`,
                background: "var(--zone-urgent-soft)",
              }}
            />
            <div
              className={styles.band}
              style={{
                left: `${valueToPct(draft.urgent_low)}%`,
                width: `${valueToPct(draft.target_low) - valueToPct(draft.urgent_low)}%`,
                background: "var(--zone-warn-soft)",
              }}
            />
            <div
              className={styles.band}
              style={{
                left: `${valueToPct(draft.target_low)}%`,
                width: `${valueToPct(draft.target_high) - valueToPct(draft.target_low)}%`,
                background: "var(--zone-ok-soft)",
              }}
            />
            <div
              className={styles.band}
              style={{
                left: `${valueToPct(draft.target_high)}%`,
                width: `${valueToPct(draft.urgent_high) - valueToPct(draft.target_high)}%`,
                background: "var(--zone-warn-soft)",
              }}
            />
            <div
              className={styles.band}
              style={{
                left: `${valueToPct(draft.urgent_high)}%`,
                right: 0,
                background: "var(--zone-urgent-soft)",
              }}
            />
            </div>
          </div>

            <div className={styles.pillRowLower}>
              {ORDER.filter((k) => HANDLE_ROW[k] === "lower").map(renderPill)}
            </div>

          </div>
        </div>

        <div className={styles.scaleRow} aria-hidden="true">
          <span>{fmt(min)}</span>
          <span>{fmt(Math.round((min + max) / 2))}</span>
          <span>{fmt(max)}</span>
        </div>

        <div
          className={`${styles.cards} ${renderSoundPicker ? styles.cardsFive : ""}`}
        >
          {ORDER.flatMap((k) => {
            const v = draft[k];
            const lo = clampForKey(k, v - STEPPER_STEP_MGDL, draft);
            const hi = clampForKey(k, v + STEPPER_STEP_MGDL, draft);
            const minusDisabled = lo === v;
            const plusDisabled = hi === v;
            const card = (
              <div
                key={k}
                className={styles.card}
                tabIndex={0}
                role="group"
                aria-label={`${META[k].cardLabel} threshold`}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft" || e.key === "-") {
                    e.preventDefault();
                    stepKey(k, e.shiftKey ? -5 : -1);
                    return;
                  }
                  if (e.key === "ArrowRight" || e.key === "+" || e.key === "=") {
                    e.preventDefault();
                    stepKey(k, e.shiftKey ? 5 : 1);
                    return;
                  }
                  if (e.key === "Home") {
                    e.preventDefault();
                    // Jump to the next-threshold floor (or the min for
                    // urgent_low).
                    const floor =
                      k === "urgent_low"
                        ? min
                        : k === "target_low"
                          ? draft.urgent_low + MIN_GAP
                          : k === "target_high"
                            ? draft.target_low + MIN_GAP
                            : draft.target_high + MIN_GAP;
                    if (floor !== v) {
                      onCommit(k, floor);
                      setDraft((prev) => ({ ...prev, [k]: floor }));
                    }
                    return;
                  }
                  if (e.key === "End") {
                    e.preventDefault();
                    const ceil =
                      k === "urgent_high"
                        ? max
                        : k === "target_high"
                          ? draft.urgent_high - MIN_GAP
                          : k === "target_low"
                            ? draft.target_high - MIN_GAP
                            : draft.target_low - MIN_GAP;
                    if (ceil !== v) {
                      onCommit(k, ceil);
                      setDraft((prev) => ({ ...prev, [k]: ceil }));
                    }
                    return;
                  }
                }}
              >
                <div className={styles.cardLabel}>
                  <span
                    className={styles.cardDot}
                    style={{ background: META[k].color }}
                    aria-hidden="true"
                  />
                  {META[k].cardLabel}
                </div>
                {isKiosk ? (
                  <button
                    type="button"
                    className={styles.cardValueBtn}
                    style={{ color: META[k].color }}
                    aria-label={`Edit ${META[k].label}`}
                    onClick={() =>
                      setEditing((ed) =>
                        ed?.key === k ? null : { key: k, str: fmt(v) },
                      )
                    }
                  >
                    {editing?.key === k ? editing.str || "—" : fmt(v)}
                  </button>
                ) : (
                  <input
                    className={styles.cardValueInput}
                    style={{ color: META[k].color }}
                    type="text"
                    inputMode={units === "mmol/L" ? "decimal" : "numeric"}
                    aria-label={`${META[k].cardLabel} value`}
                    value={editing?.key === k ? editing.str : fmt(v)}
                    onFocus={() => setEditing({ key: k, str: fmt(v) })}
                    onChange={(e) =>
                      setEditing({ key: k, str: e.target.value })
                    }
                    onBlur={commitEdit}
                    onKeyDown={(e) => {
                      // Don't let arrow keys reach the card's stepping
                      // handler while typing — let the input own them.
                      e.stopPropagation();
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                  />
                )}
                <div className={styles.stepperRow}>
                  <button
                    type="button"
                    className={styles.stepBtn}
                    aria-label={`Decrease ${META[k].label}`}
                    disabled={minusDisabled}
                    onPointerDown={onStepPointerDown(k, -STEPPER_STEP_MGDL)}
                    onPointerUp={onStepRelease}
                    onPointerCancel={onStepRelease}
                    onPointerLeave={onStepRelease}
                    onBlur={onStepRelease}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 7 L11 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={styles.stepBtn}
                    aria-label={`Increase ${META[k].label}`}
                    disabled={plusDisabled}
                    onPointerDown={onStepPointerDown(k, +STEPPER_STEP_MGDL)}
                    onPointerUp={onStepRelease}
                    onPointerCancel={onStepRelease}
                    onPointerLeave={onStepRelease}
                    onBlur={onStepRelease}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      aria-hidden="true"
                    >
                      <path
                        d="M7 3 L7 11 M3 7 L11 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
                {renderSoundPicker ? renderSoundPicker(ZONE_FOR_KEY[k]) : null}
              </div>
            );
            // Cycle 242: insert the "In Range" card (label + dot + static
            // range readout + sound picker, no stepper) between Low and High
            // when the picker is wired (omitted otherwise → original 4 cards).
            if (k === "target_low" && renderSoundPicker) {
              return [
                card,
                <div
                  key="inrange"
                  className={`${styles.card} ${styles.cardInRange}`}
                  role="group"
                  aria-label="In range sound"
                >
                  <div className={styles.cardLabel}>
                    <span
                      className={styles.cardDot}
                      style={{ background: "var(--zone-ok)" }}
                      aria-hidden="true"
                    />
                    In Range
                  </div>
                  <div className={styles.inRangeReadout} aria-label="In-range span">
                    {fmt(draft.target_low)} – {fmt(draft.target_high)}
                  </div>
                  <div className={styles.inRangeSub}>Recovery cue</div>
                  {renderSoundPicker("inrange")}
                </div>,
              ];
            }
            return [card];
          })}
        </div>

        {/* Cycle 129: kiosk numeric entry — the custom Numpad opens when
            a value card is tapped (the kiosk has no OS keyboard). Mobile
            + web use the native input above. */}
        {isKiosk && editing ? (
          <div className={styles.kioskNumpad}>
            <Numpad
              value={editing.str}
              onChange={(s) =>
                setEditing((ed) => (ed ? { ...ed, str: s } : ed))
              }
              allowDecimal={units === "mmol/L"}
              maxLength={units === "mmol/L" ? 5 : 3}
              unit={units}
              placeholder="—"
            />
            <button
              type="button"
              className={styles.numpadDone}
              onClick={commitEdit}
            >
              Done
            </button>
          </div>
        ) : null}
      </div>
    );
  },
);
